import { useEffect, useMemo, useState } from 'react';
import { Check, Play, Search, X } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import Alert from '../../components/Alert.jsx';
import AnimatedCounter from '../../components/AnimatedCounter.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';
import { useAdminQueues } from '../../context/AdminQueueContext.jsx';

// /admin/design-requests — admin queue for "I want a custom overlay" requests.
// Different lifecycle from access-requests: pending → in_progress → fulfilled
// (or → denied from any non-terminal state). Fulfillment requires the admin
// to paste the UUID of the overlays row they built; the server then pins it
// to the requester (private) and auto-grants access.
//
// All four status lists load up front (small tables) so the pills carry
// counts and tab switches are instant.

const STATUSES = ['pending', 'in_progress', 'fulfilled', 'denied'];
const LABELS = { pending: 'Pending', in_progress: 'In progress', fulfilled: 'Fulfilled', denied: 'Denied' };
const CHIP = { pending: 'adm-chip--amber adm-chip--pulse-amber', in_progress: 'adm-chip--blue', fulfilled: 'adm-chip--ok', denied: 'adm-chip--bad' };
const INITIAL = { pending: ' adm-initial--amber', in_progress: '', fulfilled: ' adm-initial--green', denied: ' adm-initial--pink' };
const ROW_DIM = {
  pending: 'rgba(255,171,0,0.06)',
  in_progress: 'rgba(0,212,255,0.06)',
  fulfilled: 'rgba(0,230,118,0.05)',
  denied: 'rgba(255,45,120,0.06)',
};

export default function AdminDesignRequests() {
  const { refresh: refreshQueues } = useAdminQueues();
  const [status, setStatus] = useState('pending');
  const [query, setQuery] = useState('');
  const [requests, setRequests] = useState(null); // all statuses, one list
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState(null);
  const [fulfillOpen, setFulfillOpen] = useState(null); // request id
  const [fulfillOverlayId, setFulfillOverlayId] = useState('');
  const [fulfillNotes, setFulfillNotes] = useState('');

  function load() {
    return Promise.all(STATUSES.map((s) => api.adminListDesignRequests(s)))
      .then((results) => setRequests(results.flatMap((r) => r?.requests ?? [])))
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    let alive = true;
    Promise.all(STATUSES.map((s) => api.adminListDesignRequests(s)))
      .then((results) => { if (alive) setRequests(results.flatMap((r) => r?.requests ?? [])); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);

  const counts = useMemo(() => {
    const c = { pending: 0, in_progress: 0, fulfilled: 0, denied: 0 };
    for (const r of requests ?? []) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    let list = (requests ?? []).filter((r) => r.status === status);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        `${r.requestedName ?? ''} ${r.requester?.username ?? ''} ${r.package?.name ?? ''}`
          .toLowerCase()
          .includes(q));
    }
    return list;
  }, [requests, status, query]);

  async function transition(id, target, body = {}) {
    setBusyId(id);
    setError(null);
    try {
      await api.adminTransitionDesignRequest(id, { status: target, ...body });
      setNote(`Request → ${target.replace('_', ' ')}.`);
      await load();
      refreshQueues();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
      setFulfillOpen(null);
      setFulfillOverlayId('');
      setFulfillNotes('');
    }
  }

  function metaBottom(r) {
    const bits = [`Requested ${new Date(r.requestedAt).toLocaleString()}`];
    if (r.decidedAt) {
      bits.push(`decided ${new Date(r.decidedAt).toLocaleString()}${r.decidedBy ? ` by ${r.decidedBy.username}` : ''}`);
    }
    if (r.fulfilledOverlay) bits.push(`built as ${r.fulfilledOverlay.name}`);
    if (r.notes) bits.push(`notes: ${r.notes}`);
    return bits.join('  ·  ');
  }

  return (
    <AdminShell crumb="Design Requests">
      <header className="adm-head adm-head--split adm-rv" style={{ animationDelay: '80ms' }}>
        <div>
          <h1 className="adm-head__title">Design Requests</h1>
          <p className="adm-head__sub">
            Custom-overlay build queue: pending → in progress → fulfilled. Fulfilling takes the
            UUID of the <code>overlays</code> row you built and auto-grants it to the requester.
          </p>
        </div>
        <div className="adm-stats">
          <div className="adm-stat adm-stat--amber">
            <div className="adm-stat__value"><AnimatedCounter to={counts.pending} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Pending</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--blue">
            <div className="adm-stat__value"><AnimatedCounter to={counts.in_progress} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">In progress</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--ok">
            <div className="adm-stat__value"><AnimatedCounter to={counts.fulfilled} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Fulfilled</div>
          </div>
        </div>
      </header>

      {error && <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>}

      <div className="adm-filterbar adm-rv" style={{ animationDelay: '140ms' }}>
        <div className="adm-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search requests…"
            aria-label="Search design requests"
          />
        </div>
        <div className="adm-pills" role="group" aria-label="Filter by status">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`adm-pill${s === status ? ' is-active' : ''}`}
              onClick={() => { setStatus(s); setFulfillOpen(null); }}
              aria-pressed={s === status}
            >
              {LABELS[s]} <span className="adm-pill__count">{counts[s]}</span>
            </button>
          ))}
        </div>
        <span className="adm-shown">{visible.length} of {(requests ?? []).length} shown</span>
      </div>

      {note && (
        <div className="adm-note" role="status">
          <Check size={14} aria-hidden="true" />
          {note}
        </div>
      )}

      {requests === null ? (
        <Loader label="Loading…" />
      ) : (
        <div className="adm-rv" style={{ animationDelay: '220ms' }}>
          <div className="adm-grid__rule" aria-hidden="true" style={{ marginTop: 18 }} />
          {visible.map((r, i) => (
            <div
              className="adm-dr adm-rvl"
              key={r.id}
              style={{ animationDelay: `${90 + i * 55}ms`, '--row-dim': ROW_DIM[r.status] }}
            >
              <div className="adm-dr__inner">
                <span
                  className={`adm-initial adm-dr__initial${INITIAL[r.status]}`}
                  aria-hidden="true"
                >
                  {r.requester?.username?.[0]?.toUpperCase() ?? '?'}
                </span>
                <div className="adm-dr__main">
                  <div className="adm-dr__titlerow">
                    <span className="adm-dr__name">{r.requestedName}</span>
                    <span className="adm-dr__meta">
                      {r.requester?.username ?? '(deleted)'}
                      {' · '}
                      {r.package?.name ?? '(unknown package)'}
                      {r.baseOverlay && <> · variant of {r.baseOverlay.name}</>}
                    </span>
                  </div>
                  <p className="adm-dr__desc">{r.description}</p>
                  {r.proposedVariables?.length > 0 && (
                    <details className="adm-dr__vars">
                      <summary>Proposed variables ({r.proposedVariables.length})</summary>
                      <pre>{JSON.stringify(r.proposedVariables, null, 2)}</pre>
                    </details>
                  )}
                  <div className="adm-dr__foot">{metaBottom(r)}</div>

                  {/* Inline "fulfill" form */}
                  {fulfillOpen === r.id && (
                    <div className="adm-dr__form">
                      <div className="adm-dr__form-hint">
                        Paste the UUID of the <span style={{ color: 'var(--neon-blue)' }}>overlays</span> row
                        you built — it must be in the same package. It will be pinned to{' '}
                        {r.requester?.username ?? 'the requester'} and access auto-granted.
                      </div>
                      <input
                        type="text"
                        className="adm-uinput adm-uinput--mono"
                        value={fulfillOverlayId}
                        onChange={(e) => setFulfillOverlayId(e.target.value)}
                        placeholder="Overlay UUID — 00000000-0000-0000-0000-000000000000"
                        disabled={busyId === r.id}
                        aria-label="Overlay UUID"
                      />
                      <input
                        type="text"
                        className="adm-uinput"
                        value={fulfillNotes}
                        onChange={(e) => setFulfillNotes(e.target.value)}
                        placeholder="Notes (optional)"
                        disabled={busyId === r.id}
                        aria-label="Notes"
                      />
                      <div className="adm-dr__formrow">
                        <button
                          type="button"
                          className="adm-act adm-act--green"
                          disabled={busyId === r.id || !fulfillOverlayId.trim()}
                          onClick={() =>
                            transition(r.id, 'fulfilled', {
                              fulfilledOverlayId: fulfillOverlayId.trim(),
                              notes: fulfillNotes.trim() || null,
                            })
                          }
                        >
                          Confirm fulfilment
                        </button>
                        <button
                          type="button"
                          className="adm-act adm-act--quiet"
                          disabled={busyId === r.id}
                          onClick={() => {
                            setFulfillOpen(null);
                            setFulfillOverlayId('');
                            setFulfillNotes('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="adm-dr__status">
                  <span className={`adm-chip ${CHIP[r.status]}`}>{LABELS[r.status]}</span>
                </div>
                <div className="adm-dr__actions">
                  {r.status === 'pending' && (
                    /* Neutral/navigational, not caution: advancing a ticket to
                       in_progress has no side effects outside this queue. */
                    <button
                      type="button"
                      className="btn-icon btn-icon--primary"
                      disabled={busyId === r.id}
                      onClick={() => transition(r.id, 'in_progress')}
                      title="Start working"
                      aria-label="Start working"
                    >
                      <Play size={16} aria-hidden="true" />
                    </button>
                  )}
                  {(r.status === 'pending' || r.status === 'in_progress') && (
                    <>
                      <button
                        type="button"
                        className="btn-icon btn-icon--success"
                        disabled={busyId === r.id}
                        onClick={() => setFulfillOpen(r.id)}
                        title="Mark fulfilled…"
                        aria-label="Mark fulfilled"
                      >
                        <Check size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon--danger"
                        disabled={busyId === r.id}
                        onClick={() => transition(r.id, 'denied')}
                        title="Deny request"
                        aria-label="Deny request"
                      >
                        <X size={16} aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="adm-empty">
              {query.trim()
                ? 'No requests match your search.'
                : `No ${status === 'in_progress' ? 'in-progress' : status} requests.`}
            </div>
          )}
        </div>
      )}
    </AdminShell>
  );
}
